import posthog from 'posthog-js';

/**
 * PostHog analytics — Sprint 9.
 *
 * Initialised lazily from VITE_POSTHOG_API_KEY. If the key is not present
 * (local dev without credentials) all capture calls are no-ops.
 *
 * Usage:
 *   import { analytics } from '@/lib/posthog';
 *   analytics.capture('analysis.calculated', { projectName, tenYearNet });
 *
 * Feature flags are evaluated client-side after identify(). Call
 *   analytics.isFeatureEnabled('excel-export-enabled')
 * to gate Pro+ features.
 */

const KEY = import.meta.env.VITE_POSTHOG_API_KEY as string | undefined;
const HOST = import.meta.env.VITE_POSTHOG_HOST as string | undefined;

let initialised = false;

function init() {
  if (initialised || !KEY) return;
  posthog.init(KEY, {
    api_host: HOST ?? 'https://us.i.posthog.com',
    capture_pageview: false, // we call capturePageview manually on route changes
    capture_pageleave: true,
    persistence: 'localStorage+cookie',
  });
  initialised = true;
}

export const analytics = {
  /** Call once on app boot and after each route change. */
  capturePageview() {
    init();
    if (!KEY) return;
    posthog.capture('$pageview');
  },

  /** Identify the current user so events are tied to their profile. */
  identify(userId: string, props?: Record<string, unknown>) {
    init();
    if (!KEY) return;
    posthog.identify(userId, props);
  },

  /** Track a named event. */
  capture(event: string, props?: Record<string, unknown>) {
    init();
    if (!KEY) return;
    posthog.capture(event, props);
  },

  /** Reset identity on sign-out. */
  reset() {
    if (!KEY) return;
    posthog.reset();
  },

  /**
   * Check a feature flag. Returns false if PostHog is not initialised.
   * Flags are evaluated server-side via decide() which runs on init.
   */
  isFeatureEnabled(flag: string): boolean {
    if (!KEY) return false;
    return posthog.isFeatureEnabled(flag) ?? false;
  },
};
